import { useMutation } from '@tanstack/react-query'
import { App, Button, Flex, Result, Spin, Typography } from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { usersLoginAdminCreateMutation } from 'src/api/main'
import { LOGIN_IN_PROGRESS_KEY } from 'src/constants'
import { emailOtpLinkVerify } from 'src/features/auth/api/emailOtp'
import { useSessionContext } from 'src/hooks'
import { paths } from 'src/router'
import {
  getApiErrorMessages,
  setLocalStorageToken,
  useInlineApiErrors
} from 'src/utils'

import { schedulePasskeyPrompt } from '../components/PasskeyRecommendationModal'
import { asAuthenticatedTokens } from '../utils/authResponseAdapters'

const { Text, Title } = Typography

export const EmailOtpLinkCallback = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { message } = App.useApp()
  const { showApiErrors, errorAlert } = useInlineApiErrors()
  const { updateUserData } = useSessionContext()
  const token = searchParams.get('token')

  // The verify step has a dedicated error Result below; but once a JWT is
  // stored, a failed user fetch has no surface here — hand it to the login
  // screen, which renders the error inline (see Login's seeding effect).
  const goToLoginWithError = (error: unknown) =>
    navigate(paths.auth.login, {
      replace: true,
      state: { authErrors: getApiErrorMessages(error) }
    })

  const { mutateAsync: fetchUserAfterLogin } = useMutation({
    ...usersLoginAdminCreateMutation(),
    onSuccess: (response) => {
      sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY)
      updateUserData(response)
      message.success(t('login_success'))
      navigate(paths.home)
    },
    onError: (e) => {
      sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY)
      goToLoginWithError(e)
    }
  })

  const {
    mutate: verifyLink,
    isPending,
    isError
  } = useMutation({
    mutationFn: async (linkToken: string) => {
      const { data } = await emailOtpLinkVerify({ token: linkToken })

      return data
    },
    onSuccess: async (response) => {
      const tokens = asAuthenticatedTokens(response)
      if (tokens) {
        schedulePasskeyPrompt()
        setLocalStorageToken(tokens)
        await fetchUserAfterLogin({})
        return
      }
      goToLoginWithError(new Error('Missing tokens in link verify response'))
    },
    onError: (error) => {
      // Surface the specific API error as an inline Alert on the error Result
      // below — so the user knows whether the link was already used (400) or a
      // transient 500 happened, not just the generic "invalid or expired" copy.
      showApiErrors(error)
    }
  })

  useEffect(() => {
    if (token) verifyLink(token)
  }, [token, verifyLink])

  if (!token || isError) {
    return (
      <Flex vertical gap={24} style={{ width: '100%' }}>
        {errorAlert}

        <Result
          status="error"
          title={t('auth.emailOtp.linkCallback.errorTitle')}
          subTitle={t('auth.emailOtp.linkCallback.errorBody')}
          extra={
            <Button
              type="primary"
              onClick={() => navigate(paths.auth.login, { replace: true })}
            >
              {t('auth.emailOtp.linkCallback.backToSignIn')}
            </Button>
          }
        />
      </Flex>
    )
  }

  return (
    <Flex
      vertical
      gap={24}
      align="center"
      justify="center"
      style={{ width: '100%', minHeight: 240 }}
    >
      <Spin size="large" spinning={isPending} />
      <Title level={3} style={{ marginBottom: 0 }}>
        {t('auth.emailOtp.linkCallback.verifying')}
      </Title>
      <Text type="secondary">{t('auth.emailOtp.linkCallback.subtitle')}</Text>
    </Flex>
  )
}
